import React from 'react';
import {DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel} from '@mui/x-data-grid';
import prettyBytes from 'pretty-bytes';
import {Box, Typography, IconButton, Grid, Grid2} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import OpenWithIcon from '@mui/icons-material/OpenWith';

import {useFileContext} from '../context/FileContext';
import {FileData} from '../types';
import ConfirmationWrapper from './buttons/ConfirmationWrapper';
import {FileIcon} from 'utils/FileIcon';
import {DELETE_FILE_MSG, DELETE_FOLDER_MSG} from 'const';
import {useTranslation} from 'react-i18next';
import {dateGetSortComparator, nameGetSortComparator, numberGetComparator} from 'utils/sorting';
import {useFileMenuContext} from './context-menu/fileMenuContext';
import {DraggableRow} from './DraggableRow';

const ROW_STYLES = {
   '& .MuiDataGrid-row:hover': {
      backgroundColor: 'rgba(66, 133, 244, 0.05)',
      transition: 'background-color 0.3s',
      // cursor: 'pointer',
   },
   '& .MuiDataGrid-cell:focus': {
      outline: 'none',
      backgroundColor: 'rgba(66, 133, 244, 0.1)',
   },
   '& .MuiDataGrid-cell': {
      userSelect: 'none',
   },
   '& .MuiDataGrid-row': {
      cursor: 'default',
   },
   '& .nav-link:hover': {
      cursor: 'auto',
   },
};

const FileList: React.FC = () => {
   const {t} = useTranslation();
   const {
      currentFolder,
      sort: {sortedFiles},
      create: {openRename},
      move: {moveItem, move},
      downloadEncrypted,
      remove,
      loadFolder,
      selectedFiles,
      setSelectedFiles,
   } = useFileContext();

   const handleRowClick = async (row: any) => {
      if (row.type === 'folder') {
         await loadFolder(row.path);
      }
   };

   const {handleClick, handleClose} = useFileMenuContext();
   React.useEffect(() => handleClose, [handleClose]);

   const columns: GridColDef[] = [
      {
         field: 'name',
         headerName: 'Name',
         flex: 1,
         getSortComparator: nameGetSortComparator,
         renderCell: (params: GridRenderCellParams<FileData>) => {
            const file = params.row;

            return (
               <Box
                  display="flex"
                  alignItems="center"
                  height="100%"
                  onContextMenu={handleClick(file)}
               >
                  <IconButton
                     onClick={() => handleRowClick(params.row)}
                     className={params.row.type === 'folder' ? '' : 'nav-link'}
                  >
                     {params.row.type === 'folder' ? (
                        <FolderIcon color="secondary" aria-label="folder icon" />
                     ) : (
                        <FileIcon name={file.name} width={20} alt="file icon" />
                     )}
                  </IconButton>
                  <Typography
                     variant="body2"
                     onDoubleClick={() => handleRowClick(params.row)}
                     className="nav-link"
                     // onClick={(ev) => {
                     //    ev.stopPropagation();
                     //    ev.preventDefault();
                     //    initRenameFolder(file);
                     // }}
                  >
                     {params.value}
                  </Typography>
               </Box>
            );
         },
      },
      // {
      //    field: 'type',
      //    headerName: 'Type',
      //    width: 100,
      //    valueGetter: (val, row) => (row.type === 'folder' ? 'Folder' : 'File'),
      // },
      {
         field: 'size',
         headerName: 'Size',
         width: 100,
         headerAlign: 'center',
         align: 'center',
         valueFormatter: (val, row) => (row.type === 'file' ? prettyBytes(Number(val)) : ''),
         getSortComparator: numberGetComparator,
      },
      {
         field: 'modifiedAt',
         headerName: 'Modified Date',
         width: 180,
         headerAlign: 'center',
         align: 'center',
         valueFormatter: (val, row) =>
            row.modifiedAt ? new Date(row.modifiedAt).toLocaleString?.() : '',
         getSortComparator: dateGetSortComparator,
         cellClassName: 'cell-date',
      },
      {
         field: 'createdAt',
         headerName: 'Created Date',
         width: 180,
         headerAlign: 'center',
         align: 'center',
         valueFormatter: (val, row) =>
            row.createdAt ? new Date(row.createdAt).toLocaleString?.() : '',
         getSortComparator: dateGetSortComparator,
         cellClassName: 'cell-date',
      },
      {
         field: 'actions',
         headerName: '',
         width: 130,
         align: 'center',
         sortable: false,
         filterable: false,
         renderCell: (params: GridRenderCellParams<FileData>) => {
            const file = params.row;
            // if (file.type !== 'folder') return null;

            return (
               <Grid container>
                  <Grid item xs={3}>
                     {file.type === 'file' && (
                        <IconButton
                           className="dwnld-btn"
                           color="primary"
                           onClick={() => downloadEncrypted(file.path)}
                        >
                           <DownloadIcon />
                        </IconButton>
                     )}
                  </Grid>
                  <Grid item xs={3}>
                     <IconButton
                        size="small"
                        color="secondary"
                        onClick={(e) => {
                           e.stopPropagation();
                           openRename(file);
                        }}
                     >
                        <EditIcon fontSize="small" />
                     </IconButton>
                  </Grid>
                  <Grid item xs={3}>
                     <IconButton
                        className="move-btn"
                        onClick={() => moveItem(file)}
                        color="secondary"
                     >
                        <OpenWithIcon />
                     </IconButton>
                  </Grid>
                  <Grid item xs={3}>
                     <ConfirmationWrapper
                        onOk={() => remove(file)}
                        content={
                           t(file.type === 'file' ? DELETE_FILE_MSG : DELETE_FOLDER_MSG) +
                           '\n' +
                           file.name
                        }
                     >
                        <IconButton
                           className="remove-btn"
                           color="warning"
                           // onClick={(e) => {
                           //    e.stopPropagation();
                           //    remove(file);
                           // }}
                        >
                           <DeleteIcon />
                        </IconButton>
                     </ConfirmationWrapper>
                  </Grid>
               </Grid>
            );
         },
      },
   ];

   const rows = sortedFiles;

   // Handle selection change
   const handleSelectionChange = (selectedIds: GridRowSelectionModel) => {
      const selected = rows.filter((file) => selectedIds.includes(file.id));
      setSelectedFiles(selected);
   };

   return (
      <div
         style={{
            height: '65vh',
            width: '100%',
         }}
      >
         <DataGrid
            rows={rows}
            // rows={rows.map((row) => ({...row, id: row.id}))}
            getRowId={(row) => row.id}
            columns={columns}
            onRowDoubleClick={(params, ev) => {
               // console.debug('row dblclick');
               ev.preventDefault();
               ev.stopPropagation();
               handleRowClick(params.row);
            }}
            slots={{
               row: (props) => <DraggableRow {...props} moveRow={move} />,
            }}
            // onCellDoubleClick={(params, ev) => {
            //    console.debug('cell dblclick');
            //    ev.preventDefault();
            //    window.getSelection()?.removeAllRanges();
            //    // ev.stopPropagation();
            // }}
            onRowSelectionModelChange={handleSelectionChange}
            rowSelectionModel={selectedFiles.map((f) => f.id)}
            pageSizeOptions={[5, 10, 20]}
            showColumnVerticalBorder={false}
            sx={ROW_STYLES}
            checkboxSelection
            isRowSelectable={(r) => r.row.type !== 'folder'}
            // disableRowSelectionOnClick
            hideFooter
         />
      </div>
   );
};

export default FileList;
